import React, { useState, useCallback, useEffect } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { FileText, File, ChevronRight, Image as ImageIcon, FileSpreadsheet, MonitorPlay, Edit3 } from 'lucide-react';
import Hero from '../components/Hero';
import ConversionGrid from '../components/ConversionGrid';
import { CONVERSIONS } from '../data';
import { FAQ_DATA } from '../faqData';
import DropZone from '../components/DropZone';
import OptionsChips from '../components/OptionsChips';
import ProgressBar from '../components/ProgressBar';
import ResultBox from '../components/ResultBox';
import ConvertButton from '../components/ConvertButton';
import QualityBar from '../components/QualityBar';
import RotationEditor from '../components/RotationEditor';
import PDFEditor from '../components/PDFEditor';
import PDFMerge from '../components/PDFMerge';
import PDFSplit from '../components/PDFSplit';
import PDFRemovePages from '../components/PDFRemovePages';
import PDFOrganize from '../components/PDFOrganize';
import PDFProtect from '../components/PDFProtect';
import PDFUnlock from '../components/PDFUnlock';
import PDFSign from '../components/PDFSign';
import { API_BASE } from '../apiConfig';

const styles = {
  workArea: (isMobile) => ({
    background: 'var(--surface)',
    border: '1px solid var(--border)',
    borderRadius: 'var(--radius-md)',
    padding: isMobile ? '16px' : '28px',
    marginBottom: '32px',
    boxShadow: 'var(--shadow)',
    backdropFilter: 'blur(18px)',
  }),
  workHeader: (isMobile) => ({
    display: 'flex',
    alignItems: 'center',
    gap: '20px',
    padding: '0 0 20px 0',
    borderBottom: '1px solid var(--border)',
    marginBottom: '32px',
    flexDirection: isMobile ? 'column' : 'row',
    justifyContent: 'space-between',
  }),
  headerBreadcrumbs: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    flexWrap: 'wrap',
  },
  headerItem: (color) => ({
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    color: color,
  }),
  headerItemText: (textColor, isMobile) => ({
    fontWeight: 700,
    fontSize: isMobile ? '13px' : '15px',
    color: textColor,
  }),
  headerTitle: (isMobile) => ({
    fontSize: isMobile ? '13px' : '15px',
    fontWeight: 600,
    color: 'var(--text)',
  }),
  headerIconBtn: {
    padding: '8px',
    borderRadius: '10px',
    border: '1px solid var(--border-md)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'var(--surface)',
    boxShadow: '0 2px 4px rgba(0,0,0,0.02)',
  },
  noConvWrap: {
    textAlign: 'center',
    padding: '32px 20px',
  },
  errorBox: {
    background: 'rgba(220, 38, 26, 0.08)',
    border: '1px solid rgba(220, 38, 26, 0.18)',
    borderRadius: '12px',
    padding: '14px 16px',
    marginBottom: '12px',
    color: 'var(--danger)',
  },
  faqSection: {
    marginTop: '48px',
    padding: '32px',
    background: 'var(--surface2)',
    borderRadius: 'var(--radius-md)',
    border: '1px solid var(--border)',
  },
  faqItem: {
    marginBottom: '24px',
  },
  faqQuestion: {
    fontSize: '16px',
    fontWeight: 600,
    color: 'var(--text)',
    marginBottom: '8px',
  },
  faqAnswer: {
    fontSize: '14px',
    color: 'var(--text-muted)',
    lineHeight: 1.6,
  },
  relatedSection: {
    marginTop: '64px',
    marginBottom: '48px',
  },
  relatedGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
    gap: '16px',
    marginTop: '20px',
  },
  relatedCard: {
    padding: '16px',
    background: 'var(--surface)',
    border: '1px solid var(--border)',
    borderRadius: 'var(--radius)',
    textDecoration: 'none',
    transition: 'transform 0.2s, border-color 0.2s',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    textAlign: 'center',
  }
};

export default function Home() {
  const navigate = useNavigate();
  const { slug } = useParams();

  const [conversion, setConversion] = useState(null);
  const [file, setFile] = useState(null);
  const [files, setFiles] = useState([]);
  const [fileRotations, setFileRotations] = useState([]); // Array of angles
  const [selectedPages, setSelectedPages] = useState([]); // For split
  const [progress, setProgress] = useState(0);
  const [status, setStatus] = useState('idle');
  const [errorMsg, setErrorMsg] = useState('');
  const [isEncrypted, setIsEncrypted] = useState(false);
  const [password, setPassword] = useState('');
  const [downloadUrl, setDownloadUrl] = useState(null);
  const [downloadName, setDownloadName] = useState('');
  const [selectedOptions, setSelectedOptions] = useState([]);
  const [quality, setQuality] = useState(80);
  const [sortOrder, setSortOrder] = useState('asc'); // 'asc' or 'desc'
  const [signatures, setSignatures] = useState([]); // For Sign PDF
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // On mount (or URL change), resolve slug → conversion
  useEffect(() => {
    if (slug) {
      const matched = CONVERSIONS.find((c) => c.slug === slug);
      if (matched) {
        setConversion(matched);
        setSelectedOptions(matched.options || []);
      } else {
        setConversion(null);
      }
    } else {
      setConversion(null);
    }
  }, [slug]);

  // SEO: Update tags and Inject Schema.org JSON-LD
  useEffect(() => {
    const baseUrl = 'https://convertalldocs.com';
    const currentPath = slug ? `/${slug}` : '';
    const canonicalUrl = `${baseUrl}${currentPath}`;

    // 1. Update Canonical
    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.rel = 'canonical';
      document.head.appendChild(canonical);
    }
    canonical.href = canonicalUrl;

    // 2. Update OG Tags
    let ogTitle = document.querySelector('meta[property="og:title"]');
    if (!ogTitle) {
      ogTitle = document.createElement('meta');
      ogTitle.setAttribute('property', 'og:title');
      document.head.appendChild(ogTitle);
    }
    
    let ogUrl = document.querySelector('meta[property="og:url"]');
    if (!ogUrl) {
      ogUrl = document.createElement('meta');
      ogUrl.setAttribute('property', 'og:url');
      document.head.appendChild(ogUrl);
    }
    ogUrl.content = canonicalUrl;

    // 3. Update Title and Description
    const title = conversion 
      ? `${conversion.label} Converter - Convert All Docs`
      : 'Convert All Docs - Free Online File Converter';
    
    const description = conversion
      ? `Free online ${conversion.from} to ${conversion.to} converter. ${conversion.desc}. No signup required. Secure and private.`
      : 'Convert All Docs is a free, secure, and fully private online file converter supporting PDFs, Images, Documents, and Data formats.';

    document.title = title;
    ogTitle.content = title;
    
    let metaDesc = document.querySelector('meta[name="description"]');
    if (!metaDesc) {
      metaDesc = document.createElement('meta');
      metaDesc.name = 'description';
      document.head.appendChild(metaDesc);
    }
    metaDesc.content = description;

    // 4. Inject JSON-LD Schema
    const existingScript = document.getElementById('schema-ld');
    if (existingScript) existingScript.remove();

    const script = document.createElement('script');
    script.id = 'schema-ld';
    script.type = 'application/ld+json';

    const faqs = FAQ_DATA[slug] || FAQ_DATA['default'];
    
    const schemaData = {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "SoftwareApplication",
          "name": conversion ? conversion.label : "Convert All Docs",
          "operatingSystem": "Any",
          "applicationCategory": "MultimediaApplication",
          "offers": {
            "@type": "Offer",
            "price": "0",
            "priceCurrency": "USD"
          },
          "description": description
        },
        {
          "@type": "FAQPage",
          "mainEntity": faqs.map(f => ({
            "@type": "Question",
            "name": f.q,
            "acceptedAnswer": {
              "@type": "Answer",
              "text": f.a
            }
          }))
        }
      ]
    };

    script.text = JSON.stringify(schemaData);
    document.head.appendChild(script);

  }, [conversion, slug]);

  const handleSelectConversion = useCallback((conv) => {
    setConversion(conv);
    setFile(null);
    setFiles([]);
    setFileRotations([]);
    setSelectedPages([]);
    setPassword('');
    setProgress(0);
    setStatus('idle');
    setErrorMsg('');
    setIsEncrypted(false);
    setDownloadUrl(null);
    setSelectedOptions(conv?.options || []);
    setQuality(80);
    setSortOrder('asc');
    setSignatures([]);
    if (conv?.slug) navigate(`/${conv.slug}`);
    else navigate('/');

    setTimeout(() => {
      document.getElementById('work-area')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 100);
  }, [navigate]);

  const handleFile = useCallback(async (f) => {
    setIsEncrypted(false);
    setErrorMsg('');
    if (conversion?.id === 'pdf-merge' || conversion?.id === 'pdf-organize') {
      const newFiles = Array.isArray(f) ? f : [f];
      setFiles(prev => {
        const wrapped = newFiles.map((file, i) => ({
          file,
          id: Math.random().toString(36).substr(2, 9),
          colorIdx: (prev.length + i) % 6
        }));
        return [...prev, ...wrapped];
      });
      setFileRotations(prev => [...prev, ...newFiles.map(() => 0)]);
    } else if (conversion?.id === 'pdf-split' || conversion?.id === 'pdf-remove' || conversion?.id === 'pdf-sign') {
      const singleFile = Array.isArray(f) ? f[0] : f;
      setFile(singleFile);
      // Fetch page count to init rotations for each page
      try {
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 60000); // 60s for inspect
        
        const formData = new FormData();
        formData.append('file', singleFile);
        const res = await fetch(`${API_BASE}/convert/inspect`, { 
          method: 'POST', 
          body: formData,
          signal: controller.signal 
        });
        clearTimeout(timeoutId);
        
        if (res.ok) {
          const data = await res.json();
          setFileRotations(Array(data.totalPages).fill(0));
          if (conversion?.id === 'pdf-split') {
            setSelectedPages(Array.from({ length: data.totalPages }, (_, i) => i));
          } else {
            setSelectedPages([]); // Start empty for removal
          }
        }
      } catch (e) {
        console.error("Inspect failed", e);
      }
    } else {
      setFile(f);
    }
    setProgress(0);
    setStatus('idle');
    setErrorMsg('');
    setDownloadUrl(null);
  }, [conversion]);

  const handleRemove = useCallback((idx = null) => {
    if ((conversion?.id === 'pdf-merge' || conversion?.id === 'pdf-organize') && idx !== null) {
      setFiles(prev => prev.filter((_, i) => i !== idx));
      setFileRotations(prev => prev.filter((_, i) => i !== idx));
    } else {
      setFile(null);
      setFiles([]);
      setFileRotations([]);
      setSelectedPages([]);
      setPassword('');
      setSignatures([]);
      setIsEncrypted(false);
    }
    setProgress(0);
    setStatus('idle');
  }, [conversion]);

  const handleRotateFile = useCallback((idx) => {
    setFileRotations(prev => {
      const next = [...prev];
      next[idx] = (next[idx] + 90) % 360;
      return next;
    });
  }, []);

  const handleSortFiles = useCallback(() => {
    if (files.length < 2) return;
    
    const combined = files.map((f, i) => ({ file: f, rot: fileRotations[i] }));
    
    // Sort logic
    if (sortOrder === 'asc') {
      combined.sort((a, b) => a.file.name.localeCompare(b.file.name, undefined, { sensitivity: 'base' }));
      setSortOrder('desc');
    } else {
      combined.sort((a, b) => b.file.name.localeCompare(a.file.name, undefined, { sensitivity: 'base' }));
      setSortOrder('asc');
    }
    
    const sortedFiles = combined.map(c => c.file);
    const sortedRots = combined.map(c => c.rot);
    
    setFiles(sortedFiles);
    setFileRotations(sortedRots);
  }, [files, fileRotations, sortOrder]);

  const handleReorderFiles = useCallback((fromIdx, toIdx) => {
    setFiles(prev => {
      const next = [...prev];
      const item = next[fromIdx];
      next.splice(fromIdx, 1);
      next.splice(toIdx, 0, item);
      return next;
    });
    setFileRotations(prev => {
      const next = [...prev];
      const item = next[fromIdx];
      next.splice(fromIdx, 1);
      next.splice(toIdx, 0, item);
      return next;
    });
  }, []);

  const handleSetSequence = useCallback((seq) => {
    setSelectedOptions([`Sequence: ${seq}`]);
  }, []);

  const handleConvert = useCallback(async (overrideRotation = null, overrideOptions = null) => {
    const hasFiles = (conversion?.id === 'pdf-merge' || conversion?.id === 'pdf-organize') ? files.length > 0 : !!file;
    if (!hasFiles || !conversion || status === 'converting') return;

    if (downloadUrl) URL.revokeObjectURL(downloadUrl);

    setStatus('converting');
    setProgress(10);
    setErrorMsg('');

    const timer = setInterval(() => {
      setProgress((p) => {
        if (p >= 95) return p;
        const diff = 95 - p;
        // Move by 2% of the remaining distance every 500ms for a natural slowdown
        return p + (diff * 0.02);
      });
    }, 500);

    try {
      const formData = new FormData();
      
      if (conversion.id === 'pdf-merge' || conversion.id === 'pdf-organize') {
        files.forEach(obj => formData.append('files', obj.file));
        formData.append('rotations', JSON.stringify(fileRotations));
      } else {
        formData.append('files', file);
        if (conversion.id === 'pdf-split' || conversion.id === 'pdf-remove') {
          formData.append('rotations', JSON.stringify(fileRotations));
        }
      }

      const finalOptions = overrideOptions ? [...overrideOptions] : [...selectedOptions];
      
      if (conversion.id === 'pdf-split' || conversion.id === 'pdf-remove') {
        finalOptions.push(`Pages: ${selectedPages.join(',')}`);
      }
      if (password) {
        finalOptions.push(`Password: ${password}`);
      }
      if (conversion.id === 'pdf-compress') {
        finalOptions.push(`Quality ${quality}%`);
      }
      if (conversion.id === 'pdf-rotate' && overrideRotation !== null) {
        finalOptions.push(`Angle ${overrideRotation}`);
      }
      formData.append('options', JSON.stringify(finalOptions));

      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 300000); // 300s for large conversions

      const response = await fetch(
        `${API_BASE}/convert/${conversion.id}`,
        {
          method: 'POST',
          body: formData,
          signal: controller.signal,
        }
      );
      
      clearTimeout(timeoutId);
      clearInterval(timer);

      if (!response.ok) {
        let msg = `Conversion failed (Status: ${response.status})`;
        try {
          const contentType = response.headers.get("content-type");
          if (contentType && contentType.includes("application/json")) {
            const err = await response.json();
            msg = err.detail || msg;
          } else {
            const text = await response.text();
            if (text) msg += `: ${text.substring(0, 100)}`;
          }
        } catch (_) {}
        throw new Error(msg);
      }

      const blob = await response.blob();
      const url = URL.createObjectURL(blob);

      const base = (conversion.id === 'pdf-merge' || conversion.id === 'pdf-organize') 
        ? (files.length > 0 ? files[0].file.name.replace(/\.[^.]+$/, '') : 'organized')
        : (file ? file.name : 'output').replace(/\.[^.]+$/, '');
      const ext = conversion.toExt || '';
      const outName = `${base}${ext}`;

      setDownloadUrl(url);
      setDownloadName(outName);
      setProgress(100);
      setStatus('done');
    } catch (err) {
      clearInterval(timer);
      setStatus('error');
      setErrorMsg(err.message);
      setProgress(0);
      if (err.message.toLowerCase().includes('password')) {
        setIsEncrypted(true);
      } else if (err.name === 'AbortError') {
        setErrorMsg('Request timed out. The backend might be busy or unreachable.');
      } else if (err.name === 'TypeError' && err.message.includes('fetch')) {
        setErrorMsg(`Cannot connect to backend at ${API_BASE}. (Error: ${err.message}). Ensure the server is running with --host 0.0.0.0 and your firewall allows port 8000.`);
      } else {
        setErrorMsg(`Error: ${err.message}`);
      }
    }
  }, [file, files, fileRotations, selectedPages, password, conversion, status, downloadUrl, selectedOptions, quality]);

  const handleDownload = useCallback(() => {
    if (!downloadUrl) return;

    const a = document.createElement('a');
    a.href = downloadUrl;
    a.download = downloadName || 'converted-file';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  }, [downloadUrl, downloadName]);

  const handleReset = useCallback(() => {
    if (downloadUrl) URL.revokeObjectURL(downloadUrl);
    setFile(null);
    setFiles([]);
    setFileRotations([]);
    setSelectedPages([]);
    setPassword('');
    setProgress(0);
    setStatus('idle');
    setErrorMsg('');
    setDownloadUrl(null);
    setDownloadName('');
    setSelectedOptions([]);
    setSortOrder('asc');
    setSignatures([]);
  }, [downloadUrl]);

  const canConvert = (() => {
    if (!conversion || status !== 'idle') return false;
    if (conversion.id === 'pdf-merge') return files.length > 1;
    if (conversion.id === 'pdf-organize') {
      const hasSequence = selectedOptions.some(opt => opt.startsWith('Sequence: ') && opt.length > 15);
      return files.length > 0 && hasSequence;
    }
    return !!file;
  })();

  const relatedTools = conversion 
    ? CONVERSIONS.filter(c => c.category === conversion.category && c.id !== conversion.id).slice(0, 4)
    : CONVERSIONS.slice(0, 4);

  const faqs = (slug && FAQ_DATA[slug]) ? FAQ_DATA[slug] : FAQ_DATA['default'];

  return (
    <>
      <Hero />

      <ConversionGrid
        selected={conversion}
        onSelect={handleSelectConversion}
      />

      <div id="work-area" style={styles.workArea(isMobile)}>
        {conversion ? (
          <>
            <div style={styles.workHeader(isMobile)}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <div style={{ padding: '8px', borderRadius: '10px', background: '#fef2f2', border: '1px solid #fee2e2' }}>
                  <FileText size={24} color="#dc2626" />
                </div>
                <ChevronRight size={16} color="#94a3b8" />
                <div style={{ padding: '8px', borderRadius: '10px', background: '#eff6ff', border: '1px solid #dbeafe' }}>
                  <File size={24} color="#2563eb" />
                </div>
              </div>
                <div style={{ marginLeft: isMobile ? 0 : '10px' }}>
                  <h1 style={{ fontSize: '18px', fontWeight: 700, color: 'var(--text)', margin: 0 }}>
                    {conversion.label} Converter
                  </h1>
                  <div style={{ fontSize: '13px', color: 'var(--text-muted)', marginTop: '2px' }}>
                    {conversion.desc}
                  </div>
                </div>
            </div>

            {isEncrypted && file && status !== 'done' ? (
              <PDFUnlock 
                file={file} 
                onPasswordChange={setPassword}
                onUnlock={() => {
                  setErrorMsg('');
                  handleConvert();
                }}
              />
            ) : conversion.id === 'pdf-rotate' && file && status !== 'done' ? (
              <RotationEditor 
                file={file} 
                status={status}
                onConvert={(angle) => handleConvert(angle)} 
              />
            ) : conversion.id === 'pdf-edit' && file && status !== 'done' ? (
              <PDFEditor 
                file={file} 
                onFinish={(edits) => {
                  setConversion({ id: 'pdf-edit', label: 'PDF Editor', toExt: '.pdf' });
                  const newOptions = [`Edits: ${JSON.stringify(edits)}`];
                  setSelectedOptions(newOptions);
                  handleConvert(null, newOptions);
                }}
              />
            ) : conversion.id === 'pdf-merge' && (files.length > 0 || status === 'done') ? (
              <>
                {status !== 'done' && (
                  <PDFMerge 
                    files={files} 
                    rotations={fileRotations}
                    onAddFiles={handleFile} 
                    onRemoveFile={handleRemove} 
                    onRotateFile={handleRotateFile}
                    onSortFiles={handleSortFiles}
                    onReorderFiles={handleReorderFiles}
                    sortOrder={sortOrder}
                  />
                )}
                
                {status === 'converting' && (
                  <ProgressBar progress={progress} />
                )}

                {status === 'error' && (
                  <div style={styles.errorBox}>
                    Error: {errorMsg}
                  </div>
                )}

                {status === 'done' ? (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                    <DropZone conversion={conversion} file={files[0]?.file} onRemove={handleReset} />
                    <ResultBox conversion={conversion} file={files[0]?.file} onDownload={handleDownload} />
                    <DropZone 
                      conversion={conversion} 
                      onFile={(newFiles) => {
                        handleReset();
                        handleFile(newFiles);
                      }}
                      isResetTrigger={true}
                    />
                  </div>
                ) : (
                  <ConvertButton
                    disabled={!canConvert}
                    converting={status === 'converting'}
                    onClick={() => handleConvert()}
                  />
                )}
              </>
            ) : conversion.id === 'pdf-split' && file && status !== 'done' ? (
              <>
                <PDFSplit 
                  file={file} 
                  rotations={fileRotations}
                  onRotatePage={handleRotateFile}
                  selectedPages={selectedPages}
                  setSelectedPages={setSelectedPages}
                />
                
                {status === 'converting' && (
                  <ProgressBar progress={progress} />
                )}

                {status === 'error' && (
                  <div style={styles.errorBox}>
                    Error: {errorMsg}
                  </div>
                )}

                <ConvertButton
                  disabled={!canConvert}
                  converting={status === 'converting'}
                  onClick={() => handleConvert()}
                />
              </>
            ) : conversion.id === 'pdf-remove' && file && status !== 'done' ? (
              <>
                <PDFRemovePages 
                  file={file} 
                  rotations={fileRotations}
                  onRotatePage={handleRotateFile}
                  selectedPages={selectedPages}
                  setSelectedPages={setSelectedPages}
                />
                
                {status === 'converting' && (
                  <ProgressBar progress={progress} />
                )}

                {status === 'error' && (
                  <div style={styles.errorBox}>
                    Error: {errorMsg}
                  </div>
                )}

                <ConvertButton
                  disabled={!canConvert}
                  converting={status === 'converting'}
                  onClick={() => handleConvert()}
                />
              </>
            ) : conversion.id === 'pdf-organize' && (files.length > 0 || status === 'done') ? (
              <>
                {status !== 'done' && (
                  <PDFOrganize 
                    files={files} 
                    onAddFiles={handleFile}
                    onRemoveFile={handleRemove}
                    onReorderFiles={handleReorderFiles}
                    onSetSequence={handleSetSequence}
                  />
                )}
                
                {status === 'converting' && (
                  <ProgressBar progress={progress} totalFiles={files.length || 1} />
                )}

                {status === 'error' && (
                  <div style={styles.errorBox}>
                    Error: {errorMsg}
                  </div>
                )}

                {status === 'done' ? (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                    <DropZone conversion={conversion} file={files[0]?.file} onRemove={handleReset} />
                    <ResultBox conversion={conversion} file={files[0]?.file} onDownload={handleDownload} />
                    <DropZone 
                      conversion={conversion} 
                      onFile={(newFiles) => {
                        handleReset();
                        handleFile(newFiles);
                      }}
                      isResetTrigger={true}
                    />
                  </div>
                ) : (
                  <ConvertButton
                    disabled={!canConvert}
                    converting={status === 'converting'}
                    onClick={() => handleConvert()}
                  />
                )}
              </>
            ) : conversion.id === 'pdf-protect' && file && status !== 'done' ? (
              <>
                <PDFProtect 
                  file={file} 
                  onPasswordChange={setPassword}
                />
                
                {status === 'converting' && (
                  <ProgressBar progress={progress} totalFiles={files.length || 1} />
                )}

                {status === 'error' && (
                  <div style={styles.errorBox}>
                    Error: {errorMsg}
                  </div>
                )}

                <ConvertButton
                  disabled={!canConvert || password.length < 4}
                  converting={status === 'converting'}
                  onClick={() => handleConvert()}
                />
              </>
            ) : conversion.id === 'pdf-unlock' && file && status !== 'done' ? (
              <>
                <PDFUnlock 
                  file={file} 
                  onPasswordChange={setPassword}
                />
                
                {status === 'converting' && (
                  <ProgressBar progress={progress} totalFiles={files.length || 1} />
                )}

                {status === 'error' && (
                  <div style={styles.errorBox}>
                    Error: {errorMsg}
                  </div>
                )}

                <ConvertButton
                  disabled={!canConvert || !password}
                  converting={status === 'converting'}
                  onClick={() => handleConvert()}
                />
              </>
            ) : conversion.id === 'pdf-sign' && file && status !== 'done' ? (
              <>
                <PDFSign 
                  file={file} 
                  onFinish={(sigs) => {
                    setSignatures(sigs);
                    setSelectedOptions([`Signatures: ${JSON.stringify(sigs)}`]);
                  }}
                />
                
                {status === 'converting' && (
                  <ProgressBar progress={progress} totalFiles={files.length || 1} />
                )}

                {status === 'error' && (
                  <div style={styles.errorBox}>
                    Error: {errorMsg}
                  </div>
                )}

                <ConvertButton
                  disabled={!canConvert || signatures.length === 0}
                  converting={status === 'converting'}
                  onClick={() => handleConvert()}
                />
              </>
            ) : (
              <>
                {status !== 'done' && (
                  <DropZone
                    conversion={conversion}
                    file={file}
                    onFile={handleFile}
                    onRemove={handleRemove}
                  />
                )}

                {status === 'idle' && file && (
                  <OptionsChips
                    conversion={conversion}
                    selectedOptions={selectedOptions}
                    onChange={setSelectedOptions}
                  />
                )}

                {status === 'idle' && file && conversion.id === 'pdf-compress' && (
                  <QualityBar value={quality} onChange={setQuality} />
                )}

                {status === 'converting' && (
                  <ProgressBar progress={progress} totalFiles={files.length || 1} />
                )}

                {status === 'error' && (
                  <div style={styles.errorBox}>
                    Error: {errorMsg}
                  </div>
                )}

                {status === 'done' ? (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                    <DropZone conversion={conversion} file={file} onRemove={handleReset} />
                    <ResultBox conversion={conversion} file={file} onDownload={handleDownload} />
                    <DropZone 
                      conversion={conversion} 
                      onFile={(newFiles) => {
                        handleReset();
                        handleFile(newFiles);
                      }}
                      isResetTrigger={true}
                    />
                  </div>
                ) : (
                  <ConvertButton
                    disabled={!canConvert}
                    converting={status === 'converting'}
                    onClick={() => handleConvert()}
                  />
                )}
              </>
            )}
          </>
        ) : (
          <div style={styles.noConvWrap}>
            Select a conversion type to begin
          </div>
        )}
      </div>

      {conversion && (
        <div style={{ marginTop: '32px', marginBottom: '32px', padding: '0 24px', color: 'var(--text-muted)' }}>
          <h2 style={{ fontSize: '20px', fontWeight: 600, color: 'var(--text)', marginBottom: '16px' }}>
            Why use our {conversion.label} tool?
          </h2>
          <p style={{ lineHeight: 1.6, marginBottom: '16px', fontSize: '14px' }}>
            Convert All Docs provides an ultra-fast, secure, and fully private way to convert your <strong>{conversion.from}</strong> files directly into <strong>{conversion.to}</strong> format. Unlike other converters that store your sensitive documents on their servers for hours or days, Convert All Docs processes your <strong>{conversion.fromExt || conversion.from}</strong> files instantly and deletes all data the moment your <strong>{conversion.toExt || conversion.to}</strong> download is ready. 
          </p>
          <p style={{ lineHeight: 1.6, marginBottom: '16px', fontSize: '14px' }}>
            Our {conversion.label} converter is optimized to retain the highest possible quality. {conversion.category === 'Images' ? 'We use state-of-the-art compression algorithms to ensure your images look crisp while saving bandwidth.' : conversion.category === 'PDF Editor' ? 'Our PDF tools allow you to manipulate your documents precisely without needing expensive desktop software.' : 'We parse your document structure to ensure that formatting, tables, and layouts are preserved perfectly.'}
          </p>
          <h3 style={{ fontSize: '16px', fontWeight: 600, color: 'var(--text)', marginBottom: '12px', marginTop: '24px' }}>
            How to convert {conversion.from} to {conversion.to}
          </h3>
          <ol style={{ paddingLeft: '20px', lineHeight: 1.6, fontSize: '14px' }}>
            <li style={{ marginBottom: '8px' }}>Select or drag and drop your {conversion.from} file into the drop zone above.</li>
            <li style={{ marginBottom: '8px' }}>Adjust any optional settings to customize your {conversion.to} output.</li>
            <li style={{ marginBottom: '8px' }}>Click the convert button and wait for the process to complete instantly.</li>
            <li>Download your newly converted {conversion.to} file directly to your device.</li>
          </ol>
        </div>
      )}

      {/* FAQ Section */}
      <div style={styles.faqSection}>
        <h2 style={{ fontSize: '20px', fontWeight: 600, color: 'var(--text)', marginBottom: '24px' }}>
          Frequently Asked Questions
        </h2>
        {faqs.map((faq, idx) => (
          <div key={idx} style={styles.faqItem}>
            <div style={styles.faqQuestion}>{faq.q}</div>
            <div style={styles.faqAnswer}>{faq.a}</div>
          </div>
        ))}
      </div>

      {/* Related Tools Section */}
      <div style={styles.relatedSection}>
        <h2 style={{ fontSize: '20px', fontWeight: 600, color: 'var(--text)', marginBottom: '24px' }}>
          Related Conversion Tools
        </h2>
        <div style={styles.relatedGrid}>
          {relatedTools.map(tool => (
            <Link 
              key={tool.id} 
              to={`/${tool.slug}`} 
              style={styles.relatedCard}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-5px)';
                e.currentTarget.style.borderColor = 'var(--accent)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'none';
                e.currentTarget.style.borderColor = 'var(--border)';
              }}
            >
              <span style={{ fontSize: '14px', fontWeight: 600, color: 'var(--text)' }}>{tool.label}</span>
            </Link>
          ))}
        </div>
      </div>
    </>
  );
}
